import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { PillBadge } from "./Primitives";
import {
  ResponsiveContainer, RadialBarChart, RadialBar, PolarAngleAxis,
} from "recharts";
import {
  X, MapPin, Clock, LogOut, ArrowRight, ChevronRight, ShieldOff,
} from "lucide-react";

/**
 * SiteScorecardModal — the "why is this site red?" drill-down.
 *
 * Pulls `/metrics/site-performance/{site_id}/detail` and shows:
 *   • 4 radial rings (Overall / On-time / Reliability / Fence)
 *   • Latest 5 offenders in each of the 3 categories that drag the score
 *
 * Opens when a Best/Worst row on <SitePerformanceCard/> is clicked.
 * Includes a "Go to site page" button for the full CRM view.
 */

function scoreTone(score) {
  if (score == null) return "#3F3F46";
  if (score >= 85) return "#34C759";
  if (score >= 70) return "#0A84FF";
  if (score >= 55) return "#FF9F0A";
  return "#FF3B30";
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function ScorecardDelta({ pct }) {
  if (pct === null || pct === undefined) {
    return (
      <span
        data-testid="scorecard-delta"
        className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 border border-[#27272A] px-1.5 py-1"
        title="No prior window data"
      >
        NEW
      </span>
    );
  }
  const val = Number(pct);
  if (Math.abs(val) < 0.05) {
    return (
      <span
        data-testid="scorecard-delta"
        className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 border border-[#27272A] px-1.5 py-1"
        title="No change vs previous window"
      >
        — 0%
      </span>
    );
  }
  const positive = val > 0;
  return (
    <span
      data-testid="scorecard-delta"
      className={`inline-flex items-center gap-0.5 text-[11px] font-mono tabular-nums px-1.5 py-1 border ${
        positive
          ? "text-[#34C759] border-[#34C7594D]"
          : "text-[#FF3B30] border-[#FF3B304D]"
      }`}
      title={`${positive ? "Up" : "Down"} ${Math.abs(val).toFixed(1)}% vs previous window`}
    >
      {positive ? "▲" : "▼"} {Math.abs(val).toFixed(1)}%
    </span>
  );
}

function Ring({ label, value, testid }) {
  const tone = scoreTone(value);
  const numeric = value == null ? null : Math.round(value);
  const data = [{ name: label, value: numeric ?? 0, fill: tone }];
  return (
    <div className="flex flex-col items-center" data-testid={testid}>
      <div className="relative w-[110px] h-[110px]">
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart
            innerRadius="72%"
            outerRadius="100%"
            data={data}
            startAngle={90}
            endAngle={-270}
          >
            <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
            <RadialBar dataKey="value" cornerRadius={4} background={{ fill: "#27272A" }} />
          </RadialBarChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="font-display text-2xl font-black tabular-nums" style={{ color: tone }}>
            {numeric ?? "—"}
          </div>
          <div className="text-[9px] uppercase tracking-widest text-zinc-500">/ 100</div>
        </div>
      </div>
      <div className="mt-2 text-[10px] uppercase tracking-widest text-zinc-400">
        {label}
      </div>
    </div>
  );
}

function OffenderRow({ icon: Icon, label, when, guard_id, extra, testid, onClose }) {
  const disabled = !guard_id;
  return (
    <Link
      to={guard_id ? `/guards?guard=${guard_id}` : "#"}
      onClick={() => { if (guard_id && onClose) onClose(); }}
      aria-disabled={disabled ? "true" : undefined}
      className={`flex items-center gap-3 px-3 py-2 border border-[#27272A] bg-[#0A0A0A] transition-colors group ${
        disabled
          ? "opacity-60 pointer-events-none"
          : "hover:bg-[#1c1c1e] hover:border-zinc-500"
      }`}
      data-testid={testid}
      title={guard_id ? "Open guard profile" : "Officer record not found"}
    >
      <Icon className="size-3.5 text-zinc-500 shrink-0 group-hover:text-zinc-200" />
      <div className="flex-1 min-w-0">
        <div className="text-xs text-zinc-200 truncate group-hover:text-white">{label}</div>
        <div className="text-[10px] text-zinc-500 truncate">{when}</div>
      </div>
      {extra && (
        <div className="text-[10px] font-mono text-zinc-400 tabular-nums shrink-0">
          {extra}
        </div>
      )}
      <ChevronRight className="size-3 text-zinc-600 group-hover:text-zinc-300 shrink-0" />
    </Link>
  );
}

export default function SiteScorecardModal({ siteId, range, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const params = {};
        if (range?.start) params.start = range.start;
        if (range?.end) params.end = range.end;
        const r = await api.get(`/metrics/site-performance/${siteId}/detail`, { params });
        if (!cancelled) { setData(r.data); setErr(false); }
      } catch {
        if (!cancelled) setErr(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [siteId, range?.start, range?.end]);

  if (!siteId) return null;

  const site = data?.site;
  const breaches = data?.recent_breaches || [];
  const missed = data?.recent_missed_clockouts || [];
  const lates = data?.recent_lates || [];
  const noOffenders = !loading && !err && breaches.length === 0 && missed.length === 0 && lates.length === 0;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center pt-16 px-4 pb-8 overflow-y-auto"
      data-testid="site-scorecard-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-3xl border border-[#27272A] bg-[#0F0F0F] shadow-2xl">
        {/* Header */}
        <div className="px-5 py-4 border-b border-[#27272A] flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="label-mute">Site Scorecard</div>
            <div className="font-display uppercase font-bold text-xl mt-1 truncate">
              {site?.site_name || "…"}
            </div>
            {site?.client_name && (
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                {site.client_name}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {site && (
              <>
                <PillBadge tone={
                  site.coverage_score == null ? "default"
                    : site.coverage_score >= 85 ? "green"
                    : site.coverage_score >= 70 ? "blue"
                    : site.coverage_score >= 55 ? "amber"
                    : "red"
                }>
                  {site.coverage_score ?? "—"} / 100
                </PillBadge>
                <ScorecardDelta pct={site.score_delta_pct} />
              </>
            )}
            <button
              onClick={onClose}
              data-testid="site-scorecard-close"
              className="p-1.5 border border-[#27272A] hover:bg-[#1c1c1e] transition-colors"
              aria-label="Close"
            >
              <X className="size-3.5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-6">
          {loading && (
            <div className="text-center py-12 text-zinc-500 scanlines">Loading scorecard…</div>
          )}
          {err && (
            <div className="text-center py-8 text-zinc-500">Failed to load site detail.</div>
          )}

          {!loading && !err && site && (
            <>
              {/* Radial rings */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 py-2" data-testid="site-scorecard-rings">
                <Ring label="Overall"     value={site.coverage_score}   testid="scorecard-ring-overall" />
                <Ring label="On-time"     value={site.on_time_rate}     testid="scorecard-ring-on-time" />
                <Ring label="Reliability" value={site.reliability_rate} testid="scorecard-ring-reliability" />
                <Ring label="Fence"       value={site.fence_rate}       testid="scorecard-ring-fence" />
              </div>

              {/* Meta row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px] uppercase tracking-widest text-zinc-500">
                <div>Shifts <span className="text-zinc-200 font-mono tabular-nums text-sm ml-1">{site.total_shifts ?? 0}</span></div>
                <div>Lates ≥30m <span className="text-zinc-200 font-mono tabular-nums text-sm ml-1">{site.late_gt30 ?? 0}</span></div>
                <div>Missed C/O <span className="text-zinc-200 font-mono tabular-nums text-sm ml-1">{site.missed_clockouts ?? 0}</span></div>
                <div>Fence breaches <span className="text-zinc-200 font-mono tabular-nums text-sm ml-1">{site.geofence_breaches ?? 0}</span></div>
              </div>

              {noOffenders ? (
                <div
                  className="border border-[#27272A] bg-[#0A0A0A] py-8 text-center text-zinc-400 text-sm"
                  data-testid="site-scorecard-empty"
                >
                  <ShieldOff className="size-4 inline mr-2 text-[#34C759]" />
                  No score-dragging events in this window. This site is clean.
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-xs text-zinc-400">
                    <span className="text-zinc-200 font-medium">Why this score</span> — the 5 most recent events in each category that pulled the score down.
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <OffenderColumn
                      title="Recent Breaches"
                      accent="#FF3B30"
                      icon={MapPin}
                      testId="scorecard-breaches"
                      onClose={onClose}
                      rows={breaches.map((b) => ({
                        key: b.id || `${b.shift_id}-${b.started_at}`,
                        label: b.guard_name || "Unknown guard",
                        when: fmtDate(b.started_at),
                        shift_id: b.shift_id,
                        guard_id: b.guard_id,
                        extra: `${b.max_distance_m ?? b.min_distance_m ?? "—"}m`,
                        rowIcon: MapPin,
                      }))}
                      emptyLabel="None"
                    />
                    <OffenderColumn
                      title="Missed Clock-outs"
                      accent="#FF9F0A"
                      icon={LogOut}
                      testId="scorecard-missed"
                      onClose={onClose}
                      rows={missed.map((m) => ({
                        key: `${m.shift_id}-${m.guard_id}`,
                        label: m.guard_name || "Unknown guard",
                        when: fmtDate(m.shift_end),
                        shift_id: m.shift_id,
                        guard_id: m.guard_id,
                        rowIcon: LogOut,
                      }))}
                      emptyLabel="None"
                    />
                    <OffenderColumn
                      title="Lates > 30 min"
                      accent="#FF9F0A"
                      icon={Clock}
                      testId="scorecard-lates"
                      onClose={onClose}
                      rows={lates.map((l) => ({
                        key: `${l.shift_id}-${l.guard_id}`,
                        label: l.guard_name || "Unknown guard",
                        when: fmtDate(l.shift_start),
                        shift_id: l.shift_id,
                        guard_id: l.guard_id,
                        extra: `${l.minutes_late}m`,
                        rowIcon: Clock,
                      }))}
                      emptyLabel="None"
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-[#27272A] flex items-center justify-between gap-3 text-xs">
          <div className="text-zinc-500">
            Window: {data?.range?.start ? fmtDate(data.range.start) : "—"} → {data?.range?.end ? fmtDate(data.range.end) : "—"}
          </div>
          {siteId && (
            <Link
              to={`/sites?site=${siteId}`}
              onClick={onClose}
              data-testid="site-scorecard-go-to-site"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#27272A] hover:bg-[#1c1c1e] text-zinc-200 hover:text-white transition-colors"
            >
              Go to site page <ArrowRight className="size-3.5" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

function OffenderColumn({ title, accent, icon: TitleIcon, rows, emptyLabel, testId, onClose }) {
  return (
    <div data-testid={testId}>
      <div className="label-mute flex items-center gap-2 mb-2" style={{ color: accent }}>
        <TitleIcon className="size-3.5" />
        {title}
        <span className="text-zinc-500 font-mono tabular-nums text-[10px] ml-auto">{rows.length}</span>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-zinc-500 py-2 border border-dashed border-[#27272A] text-center">
          {emptyLabel}
        </div>
      ) : (
        <div className="space-y-1.5">
          {rows.map((r) => (
            <OffenderRow
              key={r.key}
              icon={r.rowIcon}
              label={r.label}
              when={r.when}
              guard_id={r.guard_id}
              extra={r.extra}
              onClose={onClose}
              testid={`${testId}-row-${r.shift_id}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
